import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Select,
  FormControl,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  Pagination,
  Box,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import base64 from "base64-js";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  background: "linear-gradient(145deg, #ffffff, #f9f9f9)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

function VehicleDriverMapping() {
  
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState({});
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(5);
  const [branch, setBranch] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [authorization, setAuthorization] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData[0]);
        setCanEdit(responseData[0].uModify === 1);
        setCanSave(responseData[0].uWrite === 1);
      } else {
        console.error("Authorization failed:", response.status);
        setSnackbar({
          open: true,
          message: "Invalid authorization response.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbar({
        open: true,
        message: "An error occurred during authorization.",
        severity: "error",
      });
    }
  };

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Transportation/VehicleMaster/GetVehicleMaster`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      const vehicleList = Array.isArray(data) ? data : [];
      if (vehicleList.length === 0) {
        setVehicles([]);
        setTotalRecords(0);
        setSnackbar({
          open: true,
          message: "No vehicles found in the system.",
          severity: "info",
        });
      } else {
        const filteredVehicles = vehicleList.filter(vehicle =>
          !searchTerm ||
          vehicle.registrationNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.chassesNo?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setVehicles(filteredVehicles);
        setTotalRecords(filteredVehicles.length);
        if (filteredVehicles.length === 0) {
          setSnackbar({
            open: true,
            message: "No vehicles match your search.",
            severity: "info",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
      setTotalRecords(0);
      setSnackbar({
        open: true,
        message: "Failed to load vehicles. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const fetchDrivers = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/DriverMappingVehicle/GetDriversName`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data.Msg === "No drivers found.") {
        setDrivers([]);
      } else {
        setDrivers(data.driverNames || []);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setSnackbar({
        open: true,
        message: "Failed to load drivers",
        severity: "error",
      });
    }
  }, []);

  const fetchVehicleAssignments = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/DriverMappingVehicle/GetVechile_assignedDrivers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          SearchTerm: "",
          PageNumber: 1,
          PageSize: 10,
        }),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data.vehicles && data.vehicles.length > 0) {
        const assignments = {};
        data.vehicles.forEach(vehicle => {
          if (vehicle.employeeId && vehicle.employeeName) {
            assignments[vehicle.vechileID] = {
              employeeId: vehicle.employeeId,
              employeeName: vehicle.employeeName
            };
          }
        });
        setVehicleAssignments(assignments);
      }
    } catch (error) {
      console.error("Error fetching vehicle assignments:", error);
    }
  }, []);

  const handleDriverAssignment = useCallback((vehicleId, driverId) => {
    setVehicleAssignments(prev => ({
      ...prev,
      [vehicleId]: driverId ? drivers.find(d => d.employeeId === parseInt(driverId)) : null
    }));
  }, [drivers]);

  const handleVehicleSelection = useCallback((vehicleId, checked) => {
    setSelectedVehicles(prev => {
      if (checked) {
        return [...prev, vehicleId];
      } else {
        return prev.filter(id => id !== vehicleId);
      }
    });
  }, []);

  const fetchBranch = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });
      if (response.ok) {
        const responseData = await response.json();
        setBranch(responseData);
      }
    } catch (error) {
      console.error("Error fetching branch:", error);
    }
  };

  const checkDriverAvailability = async (employeeId) => {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    try {
      const response = await fetch(`${apiUrl}/DriverMappingVehicle/CheckDriverAvailibility?EmployeeId=${employeeId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });
      if (!response.ok) throw new Error(`Failed to check availability: ${response.status}`);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error checking driver availability:", error);
      return null;
    }
  };

  const handleSave = useCallback(async () => {
    if (!canSave) {
      setSnackbar({
        open: true,
        message: "You don't have permission to save assignments.",
        severity: "error",
      });
      return;
    }

    const vehiclesToAssign = selectedVehicles.filter(
      (vehicleId) => vehicleAssignments[vehicleId]
    );
    if (vehiclesToAssign.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select vehicles with assigned drivers",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const availabilityChecks = await Promise.all(
        vehiclesToAssign.map((vehicleId) => {
          const { employeeId } = vehicleAssignments[vehicleId];
          return checkDriverAvailability(employeeId).then((result) => ({
            vehicleId,
            employeeId,
            availability: result,
          }));
        })
      );

      const unavailable = availabilityChecks.filter(
        (entry) => entry.availability?.length > 0 && entry.availability[0].msg
      );

      if (unavailable.length > 0) {
        const errorMessages = unavailable
          .map((entry) => entry.availability[0].msg)
          .join("\n");
        setSnackbar({
          open: true,
          message: `Some drivers are already assigned:\n${errorMessages}`,
          severity: "warning",
        });
        setLoading(false);
        return;
      }

      const today = new Date().toISOString().split("T")[0];
      const payload = vehiclesToAssign.map((vehicleId) => ({
        isChecked: true,
        assignedDriverVehicleID: 0,
        branchId: branch[0]?.branchId || 0, // Dynamically use branchId from Branch/GetBranch response
        employeeId: vehicleAssignments[vehicleId].employeeId,
        vechileID: vehicleId,
        assignedDate: today,
      }));

      const response = await fetch(`${apiUrl}/DriverMappingVehicle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      const data = await response.json();
      const successfulAssignments = data.filter(
        (item) => item.msg === "Driver assigned successfully."
      );
      const failedAssignments = data.filter(
        (item) => item.msg !== "Driver assigned successfully."
      );

      if (successfulAssignments.length > 0) {
        setSnackbar({
          open: true,
          message: `Successfully assigned ${successfulAssignments.length} vehicle(s)`,
          severity: "success",
        });
        setSelectedVehicles([]);
        fetchVehicleAssignments();
      }

      if (failedAssignments.length > 0) {
        setSnackbar({
          open: true,
          message: `${failedAssignments.length} assignment(s) failed. ${
            failedAssignments[0]?.msg || ""
          }`,
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error saving assignments:", error);
      setSnackbar({
        open: true,
        message: "Failed to save assignments",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedVehicles, vehicleAssignments, branch, canSave]);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPageNumber(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPageNumber(value);
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
    fetchVehicleAssignments();
    fetchBranch();
    if (formId) {
      Authorizer();
    }
  }, [fetchVehicles, fetchDrivers, fetchVehicleAssignments, formId]);

  const totalPages = Math.ceil(totalRecords / pageSize);
  const paginatedVehicles = vehicles.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  return (
    <Container maxWidth="lg" >
       <Box
          sx={{
            backgroundColor: '#0B1F40', // dark navy blue
            padding: '12px 24px',
            boxShadow: 3,
            borderRadius: '4px 4px 0 0',
            margin: '2px auto',
           maxWidth: "100%",
          
          }}
        >
          <Typography variant="h4" sx={{ color: '#fff',  fontWeight: 'bold' }}>
        Driver Vehicle Assignment  <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6" }} />
      </Typography> </Box>


        
      <StyledPaper elevation={3} style={{marginTop:6}}>
       
        <Dialog
          open={showUserGuide}
          onClose={() => setShowUserGuide(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              borderRadius: "12px 12px 0 0",
              py: 2,
            }}
          >
            Driver Vehicle Assignment User Guide
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1898c2",
                fontWeight: "600",
                mb: 2,
                mt: 1,
                textAlign: "center",
              }}
            >
              Quick Steps to Assign Driver
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyleType: "none",
                padding: 0,
                "& li": {
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 1.5,
                  fontSize: "0.9rem",
                  color: "#333",
                },
                "& li::before": {
                  content: '"\\2022"',
                  color: "#1898c2",
                  fontWeight: "bold",
                  display: "inline-block",
                  width: "1.5em",
                  mr: 1,
                  fontSize: "1.2rem",
                },
                "& strong": {
                  margin: "0 0.2em",
                },
              }}
            >
              <li><strong>Search</strong> vehicles by registration, make, or chassis number</li>
              <li><strong>Select</strong> vehicles using checkboxes</li>
              <li><strong>Assign</strong> drivers from dropdown menus</li>
              <li><strong>Save</strong> assignments with the "Save Assignment" button</li>
              <li>Use <strong>pagination</strong> to navigate large lists</li>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <StyledButton
              onClick={() => setShowUserGuide(false)}
              color="primary"
              variant="contained"
              sx={{
                backgroundColor: "#1898c2",
                "&:hover": { backgroundColor: "#156f9a" },
                borderRadius: "8px",
                px: 4,
              }}
            >
              Close
            </StyledButton>
          </DialogActions>
        </Dialog>
        {loading && (
          <Fade in={loading}>
            <CircularProgress sx={{ display: "block", mx: "auto", mb: 2 }} />
          </Fade>
        )}
        <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
          <TextField
            label="Search Vehicles"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearch}
            sx={{ flexGrow: 1, borderRadius: 2 }}
            placeholder="Search by registration, make, model, chassis..."
          />
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            style={{height:53}}
            disabled={loading || selectedVehicles.length === 0 || !canSave}
          >
            {loading ? "Saving..." : `Save ${selectedVehicles.length} Assignment(s)`}
          </Button>
        </Box>
      </StyledPaper>
      <StyledPaper elevation={3} sx={{ mt: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color:'#127498ff' }}>
          Vehicle List & Driver Assignment
        </Typography>
      <TableContainer
  component={Paper}
  style={{
    
    marginTop: "16px",
    border: '1px solid #ccc'
  }}
>
  <Table >
    <TableHead>
      <TableRow>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Actions
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Registration No
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Make & Model
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Chassis No
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Fuel Type
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Manufacturing Date
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5', minWidth: 200 }}>
          Assign Driver
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {paginatedVehicles.map((vehicle) => (
        <TableRow
          key={vehicle.vechileID}
          sx={{
            borderTop: '1px solid #ccc',
            "&:hover": {
              backgroundColor: "#f5f5f5",
              transition: "background-color 0.2s",
            },
          }}
        >
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
            <Checkbox
              checked={selectedVehicles.includes(vehicle.vechileID)}
              onChange={(event) => {
                handleVehicleSelection(vehicle.vechileID, event.target.checked);
              }}
              disabled={loading || !canEdit}
              sx={{ "&.Mui-checked": { color: "#1976d2" } }}
            />
          </TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.registrationNo}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.make} {vehicle.model}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.chassesNo || "Not Available"}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.fuelType || "Not Available"}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
            {vehicle.dateOfMfg ? new Date(vehicle.dateOfMfg).toLocaleDateString() : "Not Available"}
          </TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
            <FormControl fullWidth size="small">
              <Select
                value={vehicleAssignments[vehicle.vechileID]?.employeeId || ""}
                onChange={(event) => handleDriverAssignment(vehicle.vechileID, event.target.value)}
                displayEmpty
                disabled={loading || !canEdit}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">
                  <em>Select Driver</em>
                </MenuItem>
                {drivers.map((driver) => (
                  <MenuItem key={driver.employeeId} value={driver.employeeId}>
                    {driver.employeeName} ({driver.employeeCode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </TableCell>
        </TableRow>
      ))}
      {vehicles.length === 0 && !loading && (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ py: 3, borderRight: '1px solid #ccc' }}>
            No vehicles found
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</TableContainer>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Vehicles: {totalRecords} | Selected: {selectedVehicles.length}
            </Typography>
          </Box>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={pageNumber}
              onChange={handlePageChange}
              color="primary"
              disabled={loading}
            />
          )}
        </Box>
      </StyledPaper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default VehicleDriverMapping;
